import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo } from 'react'
import { CampaignContext } from '../../../../contexts/CampaignContext';
import { useSidebarCollapse } from '../../../../contexts/SidebarCollapseContext'
import { Routes, Route } from 'react-router-dom';
import axiosService from '../../../../utils/AxiosService';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import InfluencerStatusColumn from './InfluencersParts/InfluencerStatusColumn';
import { toastSuccess, toastError } from '../../../FormComponents/Toasts';
import CampaignInfluencerSidebar from './InfluencersParts/CampaignInfluencerSidebar';
import InfluencerMainHeader from './InfluencersParts/InfluencerMainHeader';
import { runInAction } from 'mobx';
import { useReadOnly } from '../../../../contexts/ReadOnlyContext';

export default observer(function Influencers() {
  const { campaign, authentications } = useContext(CampaignContext)
  const readOnly = useReadOnly()
  const { isCollapsed } = useSidebarCollapse()

  const statuses = useMemo(() => campaign.statuses, [campaign.statuses, campaign.statuses.length])

  const deleteStatus = async (statusId) => {
    const affectedAuthentications = authentications.filter(auth => auth.status_id === statusId)

    if (affectedAuthentications.length > 0) {
      toastError("Cannot delete: There are influencers in this list")
      return
    }

    runInAction(() => {
      campaign.statuses = campaign.statuses.filter(status => status.id !== statusId)
    })

    axiosService.delete(
      `/api/project_manager/campaigns/${campaign.id}/campaign_authentication_statuses/${statusId}`
    ).then(() => {
      toastSuccess('List deleted successfully')
    }).catch(error => {
      runInAction(() => {
        campaign.statuses = [...statuses]
      })
      console.error(error)
      toastError('Failed to delete status')
    })
  }

  const updateAuthenticationStatus = (oldStatus, newStatus, authId) => {
    const auth = authentications.find(auth => +auth.authentication_id === +authId)
    if (!auth) return

    const originalStatus = auth.status_id

    // Use runInAction to batch our state changes
    runInAction(() => {
      auth.status_id = +newStatus
    })

    axiosService.patch(
      `/api/project_manager/campaigns/${campaign.id}/campaign_authentications/${auth.campaign_authentication_id}`,
      { campaign_authentication: { status_id: newStatus } }
    ).then(() => {
      toastSuccess(`${auth.username}'s status has been updated`)
    })
    .catch(error => {
      runInAction(() => {
        auth.status_id = +originalStatus
      })
      console.log(error)
      toastError('Failed to update status')
    })
  }

  const onDragEnd = result => {
    if (!result.destination) return

    const { destination, source, draggableId, type } = result

    if (type === 'column') {
      const newOrder = Array.from(campaign.statuses)
      const [removed] = newOrder.splice(source.index, 1)
      newOrder.splice(destination.index, 0, removed)
      campaign.statuses = newOrder
      axiosService.patch(
        `/api/project_manager/campaigns/${campaign.id}/reorder_statuses`,
        { status_order: newOrder.map(status => status.id) }
      )
      .then(response => {
        Object.assign(campaign, response)
      })
      .catch(error => {
        campaign.statuses = Array.from(campaign.statuses)
        console.error(error)
        toastError('Failed to update column order')
      })
      return
    }

    const start = source.droppableId
    const finish = destination.droppableId

    if (start === finish) return
    updateAuthenticationStatus(source.droppableId, destination.droppableId, draggableId)
  }

  return (
    <>
      <Routes>
        <Route path="campaign_authentications/:campaign_authentication_id/*" element={<CampaignInfluencerSidebar />} />
      </Routes>
      <InfluencerMainHeader />
      <div className={`flex flex-col px-4 w-full overflow-x-auto transition-all duration-300`}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns" direction="horizontal" type="column">
            {(provided) => (
              <div
                className='flex gap-x-5 pb-2 w-max'
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {statuses.map((status, index) => (
                  <Draggable
                    key={status.id}
                    draggableId={`column-${status.id}`}
                    index={index}
                    isDragDisabled={readOnly}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <InfluencerStatusColumn
                          status={status}
                          dragHandleProps={provided.dragHandleProps}
                          onDelete={deleteStatus}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {!readOnly && <InfluencerStatusColumn addNew />}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
})
